import type { Palette, PaletteMode } from '@mui/material';
import type { Persona } from '@/personas/types';
import type { ThemeTokens } from './appThemes';
import type { activityColors, childPaletteTokens, teenPaletteTokens, weekdayColor } from './theme';
import { contrastOn, ensureContrast, mix, parseColor } from './colorMath';

/**
 * Synkroniserar MUI-paletten (System A: personapaletterna i theme.ts) med det
 * valda app-temat (System B: ThemeTokens i appThemes.ts).
 *
 * Grundorsaken till "färgerna är i osynk" var att App.tsx bara slog ihop sex
 * nycklar. Allt annat — grey, secondary, backdrop, line, white/black, weekdays,
 * activities och hela palette.child/teen — låg kvar med sina egna hex och
 * renderades samtidigt på samma skärm.
 *
 * Arbetsfördelning efter den här filen:
 *   System A = semantisk källa (hue-identiteter för error/warning/success,
 *              veckodagsidentiteter, aktivitetsfärger) + bas att överlagra.
 *   System B = enda källa för neutralfamiljen (bakgrund, ytor, linjer, text).
 *
 * theme.ts rörs INTE. buildChildTheme/buildTeenTheme/getPalette behåller exakt
 * sina nuvarande värden och testas oförändrat — härledningen sker enbart här,
 * ovanpå den färdigbyggda basen.
 */

/**
 * Yta i temats kulör. I mörkt läge blandas 14 % kulör in i kortet: en tydlig men
 * dämpad ton. Tidigare togs 22 % från `bg`, vilket gav mättade, grumliga ytor
 * (teal, lila, brunt) bakom text. Det mörka lägets textSoft är ljus nog (~7:1)
 * för att klara AA även på den här ytan — appThemes räknar med den i sin grind.
 *
 * Kulören dras dessutom 12 % mot temats primary innan den läggs på bakgrunden.
 * Utan det steget blir chippen byte-identiska i alla 15 teman i mörkt läge —
 * `tk.bg` är konstanten DARK_BG — och ligger otonade på ett temat-tonat kort.
 * Det var precis den kvarvarande osynken. Ljust läge påverkas inte.
 */
const surface = (hue: string, tk: ThemeTokens, dark: boolean): string =>
  dark ? mix(tk.card, mix(hue, tk.primary, 0.12), 0.14) : mix(tk.card, hue, 0.13);

type WeekdayKey = keyof Palette['weekdays'];

const WEEKDAY_KEYS: WeekdayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const ACTIVITY_KEYS: (keyof activityColors)[] = [
  'green',
  'purple',
  'blue',
  'red',
  'yellow',
  'turquoise',
];

/**
 * Bygger ThemeTokens ur en godtycklig MUI-palett. Används när en komponent som
 * läser barn-tokens råkar renderas utanför barn-/teen-temat: då finns inget
 * app-tema att luta sig mot, men paletten bär samma information.
 */
export const tokensFromPalette = (P: Palette): ThemeTokens => ({
  bg: P.background.default,
  card: P.background.paper,
  primary: P.primary.main,
  accent: P.primary.light,
  secondaryBg: P.lineLight ?? P.background.default,
  text: P.text.primary,
  textSoft: P.text.secondary,
  border: P.divider,
});

/**
 * Barn-/teen-tokens härledda ur app-temat. Semantiska färger (orange, alert)
 * behålls från paletten — de är inlärd betydelse och får inte flytta när temat
 * byts. Allt som är en YTA härleds ur det aktiva temats kort/bakgrund.
 */
export const deriveChildTokens = (
  P: Palette,
  tk: ThemeTokens,
  mode: PaletteMode,
): childPaletteTokens => {
  const dark = mode === 'dark';
  const { r, g, b } = parseColor(tk.text);

  return {
    bg: tk.bg,
    card: tk.card,
    text: tk.text,
    textSoft: tk.textSoft,
    border: tk.border,
    primaryLight: surface(tk.primary, tk, dark),
    greenLight: surface(P.success.main, tk, dark),
    amberLight: surface(P.warning.main, tk, dark),
    orange: P.child?.orange ?? P.warning.main,
    orangeLight: surface(P.child?.orange ?? P.warning.main, tk, dark),
    redLight: surface(P.error.main, tk, dark),
    muted: mix(tk.textSoft, tk.bg, 0.15),
    mutedLight: mix(tk.card, tk.textSoft, dark ? 0.14 : 0.1),
    attentionMain: dark ? P.warning.main : mix(P.warning.main, tk.text, 0.45),
    attentionLight: surface(P.warning.main, tk, dark),
    alert: P.error.main,
    shadow: dark ? '0 2px 12px rgba(0,0,0,0.45)' : `0 2px 12px rgba(${r},${g},${b},0.08)`,
  };
};

export type SyncedPalette = {
  primary: {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
    50: string;
    100: string;
  };
  secondary: { main: string; contrastText: string };
  error: { main: string; light: string; dark: string };
  warning: { main: string; light: string; dark: string };
  success: { main: string; light: string; dark: string };
  grey: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
  background: { default: string; paper: string };
  text: { primary: string; secondary: string };
  divider: string;
  line: string;
  lineLight: string;
  lineDark: string;
  white: string;
  black: string;
  backdrop: string;
  calendarLine: string;
  weekdays: Record<WeekdayKey, weekdayColor>;
  activities: activityColors;
  child?: childPaletteTokens;
  teen?: teenPaletteTokens;
};

/**
 * Returnerar de palettnycklar som ska överlagra baseTheme.palette i App.tsx.
 */
export const syncPaletteWithTokens = (
  P: Palette,
  tk: ThemeTokens,
  mode: PaletteMode,
  persona: Persona,
): SyncedPalette => {
  const dark = mode === 'dark';
  const surf = (hue: string) => surface(hue, tk, dark);

  // A3 — primary. `dark` grindas mot `light` så att t.ex. en knapptext på en
  // primärtonad yta alltid når AA, oavsett hur ljus/mörk temats primary är.
  const primaryLight = surf(tk.primary);
  const primary = {
    main: tk.primary,
    light: primaryLight,
    dark: ensureContrast(mix(tk.primary, tk.text, 0.25), primaryLight, 4.5, tk.text),
    contrastText: contrastOn(tk.primary),
    50: dark ? mix(tk.bg, tk.primary, 0.12) : mix(tk.card, tk.primary, 0.06),
    100: primaryLight,
  };

  // A5 — veckodagarna behåller sin identitet (inlärd färgkodning), men ytan de
  // ligger på härleds ur temats kort. Onsdag är "den vita dagen" och måste
  // därför följa temats yta/linje i stället för literal vit.
  const weekdays = WEEKDAY_KEYS.reduce((acc, key) => {
    if (key === 'wednesday') {
      acc[key] = {
        main: tk.textSoft,
        border: `2px solid ${tk.border}`,
        underline: tk.border,
        light: tk.secondaryBg,
      };
    } else {
      const base = P.weekdays[key];
      acc[key] = {
        main: base.main,
        border: 'none',
        light: mix(tk.card, base.main, dark ? 0.22 : 0.14),
      };
    }
    return acc;
  }, {} as Record<WeekdayKey, weekdayColor>);

  // A6 — aktivitetsfärgerna är semantiska och lämnas orörda i ljust läge. I
  // mörkt läge lyfts luminansen med bevarad kulör (#1F8900 → #5AA745).
  const activities = ACTIVITY_KEYS.reduce((acc, key) => {
    const base = P.activities[key];
    acc[key] = dark ? mix(base, tk.text, 0.28) : base;
    return acc;
  }, {} as activityColors);

  const childTokens = persona === 'adult' ? undefined : deriveChildTokens(P, tk, mode);

  return {
    primary,
    secondary: { main: tk.secondaryBg, contrastText: tk.text },
    // A4 — error/warning/success .main och .dark är semantiska och lämnas som
    // basen satt dem. Bara .light är en yta och måste sitta på aktuellt kort.
    error: { main: P.error.main, light: surf(P.error.main), dark: P.error.dark },
    warning: { main: P.warning.main, light: surf(P.warning.main), dark: P.warning.dark },
    success: { main: P.success.main, light: surf(P.success.main), dark: P.success.dark },
    // A2 — grå-rampen. Ankarna 100/300/500 ÄR temats egna neutraler; stegen
    // däremellan interpoleras mellan ankarna i stället för att beräknas på en
    // egen kurva. En fristående kurva hamnar inte på samma linje som ankarna och
    // gav riktningsbrott (50 mörkare än 100, 200 mörkare än 300, 600 ljusare än
    // 500) i samtliga 15 teman. Toningen mot primary ligger nu implicit i
    // ankarna, som redan är primärtonade — ingen separat primary-term behövs.
    grey: {
      50: mix(tk.card, tk.secondaryBg, 0.5),
      100: tk.secondaryBg,
      200: mix(tk.secondaryBg, tk.border, 0.5),
      300: tk.border,
      400: mix(tk.border, tk.textSoft, 0.5),
      500: tk.textSoft,
      600: mix(tk.textSoft, tk.text, 0.25),
      700: mix(tk.textSoft, tk.text, 0.45),
      800: mix(tk.textSoft, tk.text, 0.7),
      900: mix(tk.textSoft, tk.text, 0.88),
    },
    background: { default: tk.bg, paper: tk.card },
    text: { primary: tk.text, secondary: tk.textSoft },
    divider: tk.border,
    line: tk.border,
    lineLight: tk.secondaryBg,
    lineDark: tk.textSoft,
    // 'white'/'black' är semantiskt SURFACE respektive INK, inte literalfärger —
    // darkPalette sätter redan white: '#18191A'.
    white: tk.card,
    black: tk.text,
    backdrop: tk.secondaryBg,
    calendarLine: tk.border,
    weekdays,
    activities,
    child: childTokens,
    teen:
      childTokens && persona === 'teen'
        ? { ...childTokens, accent: P.teen?.accent ?? tk.accent }
        : undefined,
  };
};
